// Contact.js
import React, { useState } from 'react';
import { Phone, Mail, Clock, Send, MessageSquare,Smartphone, Globe, HeadphonesIcon } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    userType: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('');

  const contactInfo = [
    {
      icon: Phone,
      title: 'Support Line',
      details: ['+254 797 310 641', 'Available 24/7']
    },
    {
      icon: Mail,
      title: 'Email Support',
      details: ['support@medigo.co.ke', 'info@medigo.co.ke']
    },
    {
      icon: Globe,
      title: 'Online Platform',
      details: ['100% Digital Service', 'Access Anywhere in Kenya']
    },
    {
      icon: Clock,
      title: 'Response Time',
      details: ['24/7 Platform Access', 'Support: Mon-Sun 8AM-10PM', 'Emergency: 911']
    }
  ];

  const departments = [
    { name: 'Patient Support', email: 'patients@medigo.co.ke', phone: '+254 797 310 641' },
    { name: 'Doctor Registration', email: 'doctors@medigo.co.ke', phone: '+254 797 310 641' },
    { name: 'Hospital Partnership', email: 'hospitals@medigo.co.ke', phone: '+254 797 310 641' },
    { name: 'Pharmacy Partnership', email: 'pharmacy@medigo.co.ke', phone: '+254 797 310 641' },
    { name: 'Emergency Services', email: 'emergency@medigo.co.ke', phone: '911' },
    { name: 'Technical Support', email: 'tech@medigo.co.ke', phone: '+254 797 310 641' }
  ];

 

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.userType) newErrors.userType = 'Please select user type';
    if (!formData.subject) newErrors.subject = 'Please select a subject';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.message.trim().length < 10) newErrors.message = 'Message must be at least 10 characters';
    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      setSubmitStatus('sending');
      // Simulate API call
      setTimeout(() => {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          userType: '',
          subject: '',
          message: ''
        });
        setTimeout(() => setSubmitStatus(''), 5000);
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h1>
          <p className="text-xl text-primary-50 max-w-2xl mx-auto mb-6">
            MediGo is an online healthcare platform connecting patients with medical providers across Kenya
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-primary-50">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <span>100% Digital Platform</span>
            </div>
            <div className="hidden sm:block">•</div>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              <span>Serving All of Kenya</span>
            </div>
            <div className="hidden sm:block">•</div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>24/7 Access</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="py-12 px-4 -mt-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-6">
          {contactInfo.map((info, index) => {
            const Icon = info.icon;
            return (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-secondary-800 mb-2">{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-12 px-4 bg-gradient-to-r from-blue-50 to-primary-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-secondary-800 mb-4">How MediGo Works</h2>
          <p className="text-gray-600 max-w-3xl mx-auto mb-8">
            MediGo is a digital healthcare platform that helps you find and connect with hospitals, doctors, 
            pharmacies, and ambulance services near you - all from your phone or computer.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="font-semibold text-secondary-800 mb-2">Find Healthcare Providers</h3>
              <p className="text-gray-600 text-sm">
                Search for hospitals, doctors, pharmacies, and ambulances in your area
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="font-semibold text-secondary-800 mb-2">Book Online</h3>
              <p className="text-gray-600 text-sm">
                Schedule consultations and appointments directly through the platform
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="font-semibold text-secondary-800 mb-2">Get Care</h3>
              <p className="text-gray-600 text-sm">
                Access healthcare services from verified providers across Kenya
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Department Contacts */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-secondary-800">Contact Our Departments</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {departments.map((dept, index) => (
              <div key={index} className="bg-white rounded-lg p-5 shadow hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-secondary-800 mb-3">{dept.name}</h3>
                <div className="space-y-2">
                  <a 
                    href={`mailto:${dept.email}`}
                    className="text-sm text-gray-600 flex items-center hover:text-primary-600 transition"
                  >
                    <Mail className="w-4 h-4 mr-2 text-primary-500 flex-shrink-0" />
                    {dept.email}
                  </a>
                  <a 
                    href={`tel:${dept.phone}`}
                    className="text-sm text-gray-600 flex items-center hover:text-primary-600 transition"
                  >
                    <Phone className="w-4 h-4 mr-2 text-primary-500 flex-shrink-0" />
                    {dept.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-secondary-800">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+254 700 000 000"
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">I am a *</label>
                  <select
                    name="userType"
                    value={formData.userType}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.userType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select type</option>
                    <option value="patient">Patient</option>
                    <option value="doctor">Doctor</option>
                    <option value="hospital">Hospital Representative</option>
                    <option value="pharmacy">Pharmacy Owner</option>
                    <option value="ambulance">Ambulance Service</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.userType && <p className="text-red-500 text-xs mt-1">{errors.userType}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Subject *</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="registration">Registration Help</option>
                    <option value="technical">Technical Support</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="complaint">Complaint</option>
                    <option value="feedback">Feedback</option>
                    <option value="emergency">Emergency Services</option>
                  </select>
                  {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.message ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Please describe how we can help you..."
                />
                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={submitStatus === 'sending'}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:bg-gray-400 flex items-center justify-center"
              >
                {submitStatus === 'sending' ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg text-center">
                  Message sent successfully! We'll get back to you within 24 hours.
                </div>
              )}
            </form>
          </div>

          {/* Additional Info */}
          <div className="space-y-6">
            {/* Quick Support */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-secondary-800">Need Immediate Help?</h3>
              <div className="space-y-3">
                <a 
                  href="tel:+254797310641" 
                  className="flex items-center p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition"
                >
                  <Phone className="w-6 h-6 text-primary-600 mr-3" />
                  <div>
                    <p className="font-semibold text-secondary-800">Call Support</p>
                    <p className="text-sm text-gray-600">+254 797 310 641</p>
                  </div>
                </a>
                <a 
                  href="tel:911" 
                  className="flex items-center p-4 bg-red-50 rounded-lg hover:bg-red-100 transition"
                >
                  <HeadphonesIcon className="w-6 h-6 text-red-600 mr-3" />
                  <div>
                    <p className="font-semibold text-secondary-800">Emergency: 911</p>
                    <p className="text-sm text-gray-600">24/7 Emergency hotline</p>
                  </div>
                </a>
                <div className="flex items-center p-4 bg-green-50 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-green-600 mr-3" />
                  <div>
                    <p className="font-semibold text-secondary-800">Email Support</p>
                    <a 
                      href="mailto:support@medigo.co.ke"
                      className="text-sm text-primary-600 hover:underline"
                    >
                      support@medigo.co.ke
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* About Platform */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-secondary-800">About MediGo</h3>
              <p className="text-gray-600 mb-4">
                MediGo is Kenya's leading online healthcare platform, making it easy to find and connect 
                with medical providers anywhere in the country.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Find hospitals, doctors, and pharmacies near you
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Book online consultations and appointments
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Request ambulance services in emergencies
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Access healthcare from anywhere in Kenya
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;